import { type FormEvent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ApiError, authApi } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useAppPreferences } from "../../lib/preferences";

export function VerifyEmailPage() {
  const { t } = useAppPreferences();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyEmail } = useAuth();
  const [email] = useState(searchParams.get("email") ?? "");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resent, setResent] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const session = await verifyEmail({
        email,
        code,
        booking_draft_id: searchParams.get("booking_draft_id"),
      });
      navigate(session.home_path, { replace: true });
    } catch (requestError) {
      if (requestError instanceof ApiError) {
        setError(requestError.detail);
      } else {
        setError(t("Unable to verify email. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setResent(false);
    setError(null);
    try {
      await authApi.resendVerification(email);
      setResent(true);
    } catch (requestError) {
      if (requestError instanceof ApiError) {
        setError(requestError.detail);
      } else {
        setError(t("Unable to resend code. Please try again."));
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <form
        className="w-full max-w-sm space-y-6"
        onSubmit={handleSubmit}
      >
        <div className="text-center">
          <h1 className="text-2xl font-bold tracking-tight text-white">
            {t("Verify your email")}
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            {t("Enter the 6-digit code sent to {{email}}", { email })}
          </p>
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-400">
              {t("Verification code")}
            </span>
            <input
              autoComplete="one-time-code"
              className="ops-input w-full text-center tracking-[0.5em]"
              inputMode="numeric"
              maxLength={6}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              required
              type="text"
              value={code}
            />
          </label>
        </div>

        {error && (
          <div
            className="rounded-xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100"
            role="alert"
          >
            {error}
          </div>
        )}

        {resent && (
          <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-100">
            {t("A new code has been sent.")}
          </div>
        )}

        <button
          className="ops-button w-full"
          data-variant="primary"
          disabled={submitting || code.length !== 6}
          type="submit"
        >
          {submitting ? t("Verifying...") : t("Verify email")}
        </button>

        <button
          className="ops-button w-full"
          data-variant="secondary"
          disabled={!email}
          onClick={handleResend}
          type="button"
        >
          {t("Resend code")}
        </button>
      </form>
    </div>
  );
}
